import type {
  CardKind,
  HandRank,
  LoveWinsAllPhase,
  LoveWinsAllPlayerId,
  RoundResult,
} from "@cloudnest/love-wins-all-engine";
import { handRank } from "@cloudnest/love-wins-all-engine";

export interface HandView {
  readonly count: number;
  /** Card kinds, or null when masked (opponent's un-revealed hand). */
  readonly cards: readonly CardKind[] | null;
}

interface LoveWinsAllViewProps {
  readonly round: number;
  readonly phase: LoveWinsAllPhase;
  readonly leadPlayerId: LoveWinsAllPlayerId;
  readonly currentPlayerId: LoveWinsAllPlayerId;
  readonly chips: Readonly<Record<LoveWinsAllPlayerId, number>>;
  readonly pot: number;
  readonly contributed: Readonly<Record<LoveWinsAllPlayerId, number>>;
  readonly hands: Readonly<Record<LoveWinsAllPlayerId, HandView | null>>;
  readonly lastRound: RoundResult | null;
  readonly names: Readonly<Record<LoveWinsAllPlayerId, string>>;
}

const PHASE_LABEL: Record<LoveWinsAllPhase, string> = {
  bet: "베팅",
  "game-over": "종료",
};

const KIND_GLYPH: Record<CardKind, string> = {
  scissors: "✌",
  rock: "✊",
  paper: "✋",
  love: "♥",
};
const KIND_LABEL: Record<CardKind, string> = {
  scissors: "가위",
  rock: "바위",
  paper: "보",
  love: "러브",
};
const RANK_LABEL: Record<HandRank, string> = {
  three: "스리",
  two: "투",
  one: "원",
};

function Card({ kind }: { kind: CardKind }) {
  return (
    <span
      data-testid="love-wins-all-card"
      data-kind={kind}
      role="img"
      aria-label={KIND_LABEL[kind]}
      className="inline-flex flex-col items-center justify-center rounded-md font-bold"
      style={{
        width: 38,
        height: 52,
        color: "#2a1530",
        background: "var(--cn-love-wins-all-card-face)",
        boxShadow: `0 0 0 2px var(--cn-love-wins-all-${kind})`,
      }}
    >
      <span style={{ fontSize: 18, color: `var(--cn-love-wins-all-${kind})` }}>
        {KIND_GLYPH[kind]}
      </span>
      <span className="text-[9px]">{KIND_LABEL[kind]}</span>
    </span>
  );
}

function CardBack() {
  return (
    <span
      data-testid="love-wins-all-card-back"
      role="img"
      aria-label="가려진 카드"
      className="inline-flex items-center justify-center rounded-md font-bold text-lg"
      style={{
        width: 38,
        height: 52,
        color: "var(--cn-love-wins-all-pink)",
        background: "var(--cn-love-wins-all-card-back)",
        boxShadow: "0 0 0 2px var(--cn-love-wins-all-border)",
      }}
    >
      ♥
    </span>
  );
}

function HandArea({
  playerId,
  name,
  hand,
  contributed,
  isLead,
  isCurrent,
}: {
  playerId: LoveWinsAllPlayerId;
  name: string;
  hand: HandView | null;
  contributed: number;
  isLead: boolean;
  isCurrent: boolean;
}) {
  const cards = hand?.cards ?? null;
  const count = hand?.count ?? 0;
  const rank = cards !== null ? handRank(cards) : null;
  return (
    <div
      data-testid={`love-wins-all-hand-${playerId}`}
      data-revealed={cards !== null ? "true" : "false"}
      data-rank={rank ?? "hidden"}
      className="flex flex-col gap-2 rounded-md border p-3"
      style={{
        borderColor: isCurrent
          ? "var(--cn-love-wins-all-gold)"
          : "var(--cn-love-wins-all-border)",
        background: "var(--cn-love-wins-all-panel)",
      }}
    >
      <div className="flex items-center justify-between text-sm">
        <span
          className="font-semibold"
          style={{ color: `var(--cn-love-wins-all-${playerId})` }}
        >
          {name}
          {isLead ? " · 선" : ""}
        </span>
        <span style={{ color: "var(--cn-love-wins-all-text-muted)" }}>
          {rank ? (
            <span style={{ color: "var(--cn-love-wins-all-three)" }}>
              {RANK_LABEL[rank]} 러브
            </span>
          ) : (
            "족보 ?"
          )}{" "}
          · 베팅 {contributed}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        {cards !== null
          ? cards.map((kind, index) => (
              <Card key={`${kind}-${index}`} kind={kind} />
            ))
          : Array.from({ length: count }, (_unused, index) => (
              <CardBack key={index} />
            ))}
      </div>
    </div>
  );
}

/**
 * Read-only match panel for 러브 윈즈 올. Renders chips, pot, both hands (face-up
 * when revealed, card backs when masked), each hand's 족보 label, and the
 * last-round reveal. The opponent's cards stay hidden in the default observer
 * view. Pure presentation, driven by props from the replay screen.
 */
export function LoveWinsAllView({
  round,
  phase,
  leadPlayerId,
  currentPlayerId,
  chips,
  pot,
  contributed,
  hands,
  lastRound,
  names,
}: LoveWinsAllViewProps) {
  return (
    <div
      data-testid="love-wins-all-board"
      className="flex w-full flex-col gap-3 rounded-md p-3"
      style={{
        background: "var(--cn-love-wins-all-panel-alt)",
        maxWidth: "min(94vw, 560px)",
      }}
    >
      <div className="flex items-center justify-between text-sm">
        <span
          className="font-semibold"
          style={{ color: "var(--cn-love-wins-all-text)" }}
        >
          라운드 {round + 1}
        </span>
        <span
          data-testid="love-wins-all-phase"
          className="rounded-full px-2 py-0.5 text-xs"
          style={{
            background: "var(--cn-love-wins-all-panel)",
            color: "var(--cn-love-wins-all-text)",
          }}
        >
          {PHASE_LABEL[phase]}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          data-testid="love-wins-all-chips-p1"
          className="font-semibold text-sm"
          style={{ color: "var(--cn-love-wins-all-p1)" }}
        >
          {names.p1} · 칩 {chips.p1}
        </span>
        <span
          data-testid="love-wins-all-pot"
          className="rounded-md px-3 py-1 font-bold text-sm"
          style={{
            background: "var(--cn-love-wins-all-pot)",
            color: "#06281c",
          }}
        >
          팟 {pot}
        </span>
        <span
          data-testid="love-wins-all-chips-p2"
          className="font-semibold text-sm"
          style={{ color: "var(--cn-love-wins-all-p2)" }}
        >
          {names.p2} · 칩 {chips.p2}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(["p1", "p2"] as const).map((playerId) => (
          <HandArea
            key={playerId}
            playerId={playerId}
            name={names[playerId]}
            hand={hands[playerId]}
            contributed={contributed[playerId]}
            isLead={leadPlayerId === playerId}
            isCurrent={currentPlayerId === playerId && phase !== "game-over"}
          />
        ))}
      </div>

      {lastRound && (
        <div
          data-testid="love-wins-all-last-round"
          className="rounded-md border px-3 py-2 text-center text-xs"
          style={{
            borderColor: "var(--cn-love-wins-all-border)",
            background: "var(--cn-love-wins-all-panel)",
            color: "var(--cn-love-wins-all-text)",
          }}
        >
          직전 라운드 {lastRound.round + 1}: {names.p1}{" "}
          {RANK_LABEL[lastRound.ranks.p1]} vs {names.p2}{" "}
          {RANK_LABEL[lastRound.ranks.p2]} ·{" "}
          {lastRound.folderId !== null ? (
            <span style={{ color: "var(--cn-love-wins-all-fold)" }}>
              {names[lastRound.folderId]} 폴드 →{" "}
            </span>
          ) : null}
          <span
            style={{
              color:
                lastRound.winnerId === "push"
                  ? "var(--cn-love-wins-all-text)"
                  : "var(--cn-love-wins-all-win)",
              fontWeight: 700,
            }}
          >
            {lastRound.winnerId === "push"
              ? "푸시"
              : `${names[lastRound.winnerId]} 라운드 승 (팟 ${lastRound.pot})`}
          </span>
        </div>
      )}
    </div>
  );
}
